import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { NumericFormat } from 'react-number-format';
import {
  FaFileSignature, FaSearch, FaPlus, FaTrash, FaCar, FaUser,
  FaPrint, FaTimes, FaCheckCircle, FaArrowLeft, FaMapMarkerAlt, FaSave,
  FaBold, FaItalic, FaUnderline, FaAlignLeft, FaAlignCenter, FaAlignRight, FaAlignJustify,
  FaUndo, FaRedo, FaEraser, FaUsers, FaCog
} from 'react-icons/fa';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import { decodeToken } from 'react-jwt';
import ModalConfiguracaoModelo from '../components/ModalConfiguracaoModelo';
import { sanitizarHtml } from '../others/sanitizarHtml';
import ConfirmModal from '../components/ConfirmModal';
import BarraCadastro from '../components/BarraCadastro';
import AvisoCadastroIncompleto from '../components/AvisoCadastroIncompleto';
import ImportarDocumento from '../components/ImportarDocumento';
import { resolverAnexo } from '../others/importacao';
import { anexarDocumentoCliente, anexarDocumentoVeiculo } from '../others/anexos';
import { situacaoCadastro, buscarClientePorDocumento, buscarVeiculoPorPlaca as buscarVeiculoCadastradoPorPlaca } from '../others/cadastroSync';

const CAMPOS_PESSOA = ['nome', 'cpfCnpj', 'rg', 'telefone', 'email', 'cep', 'logradouro', 'numero', 'complemento', 'bairro', 'cidade', 'uf'];
const CAMPOS_VEICULO_SYNC = ['placa', 'chassi', 'renavam', 'marcaModelo', 'cor', 'ano'];
const ROTULOS_CAMPO_PESSOA = {
  nome: 'nome', cpfCnpj: 'CPF/CNPJ', rg: 'RG', telefone: 'telefone', email: 'e-mail', cep: 'CEP',
  logradouro: 'logradouro', numero: 'número', complemento: 'complemento', bairro: 'bairro', cidade: 'cidade', uf: 'UF'
};
const ROTULOS_CAMPO_VEICULO = {
  placa: 'placa', chassi: 'chassi', renavam: 'RENAVAM', marcaModelo: 'marca/modelo', cor: 'cor', ano: 'ano'
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const enderecoVazio = () => ({
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: ''
});

export const pessoaVazia = () => ({
  idCliente: null,
  nome: '',
  cpfCnpj: '',
  rg: '',
  telefone: '',
  email: '',
  ...enderecoVazio()
});

export const veiculoVazio = () => ({
  idVeiculo: null,
  marcaModelo: '',
  cor: '',
  placa: '',
  chassi: '',
  renavam: '',
  ano: '',
  // Só os documentos ligados ao motor usam este campo; nos outros ele fica vazio e não vai para lugar nenhum
  motor: ''
});

export function removerMascaraCPFCNPJ(documento) {
  if (typeof documento === 'string') return documento.replace(/\D/g, '');
  if (typeof documento === 'number' || typeof documento === 'bigint') return documento.toString();
  return '';
}

export function formatarCpfCnpj(valor) {
  const num = removerMascaraCPFCNPJ(valor || '');
  if (!num) return '';
  if (num.length <= 11) {
    return num.padStart(11, '0').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return num.padStart(14, '0').replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

export function formatarCep(valor) {
  const num = (valor || '').replace(/\D/g, '').slice(0, 8);
  return num.length <= 5 ? num : `${num.slice(0, 5)}-${num.slice(5)}`;
}

// Busca de endereço via ViaCEP — nunca bloqueia o formulário se falhar ou não encontrar.
export async function buscarCep(cepValor) {
  const cep = (cepValor || '').replace(/\D/g, '');
  if (cep.length !== 8) return null;
  try {
    const resp = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
    if (resp.data && !resp.data.erro) {
      return {
        logradouro: resp.data.logradouro || '',
        bairro: resp.data.bairro || '',
        cidade: resp.data.localidade || '',
        uf: resp.data.uf || ''
      };
    }
    return null;
  } catch (error) {
    console.error('Erro ao buscar CEP:', error);
    return null;
  }
}

// ── Texto do modelo de procuração (PROCURACAO_GILO_AZERA) ──────────────────────
const PODERES_ANTES = 'podendo efetuar a venda, receber o preço, assinar requerimentos, realizar vistorias, com poderes também para requerer, junto aos CRVAS/DETRAN-RS, os processos de 2ª vias de CRV, emitir CRLV-e e ATPV-e, cancelamento de ATPV-e, retirar veículo apreendido ao guincho/CRD, baixa definitiva do veículo, solicitar vistoria lacrada, alterações de informações do veículo, troca de município, encaminhar TROCA DE PLACA (MERCOSUL), assinar requerimentos, declarações de responsabilidade, declaração, declarar endereço residencial, alteração de endereço de entrega do CRV, inclusão de comunicação de venda, inclusão ou liberação de restrição por transferência, solicitar certidões, autorização para troca de placas, e DCPPO, assinar contratos de inclusão e liberação de: alienação, locação, comodato, reserva de domínio, e ainda assinar carta de desistência de leasing, endossar documentos, reativar veículo desativado, retirar documentos junto às agências dos Correios, retirar o veículo de depósitos, usar o veículo em apreço, manejando o mesmo, em qualquer parte do território nacional ou estrangeiro, ficando cível e criminalmente responsável por qualquer acidente ou ocorrência, ';
const PODERES_NEGRITO = 'podendo transferir para si próprio';
const PODERES_DEPOIS = ', conforme Art. 685 do Código Civil, e finalmente, usar e gozar do veículo como coisa sua e sem interferência ou autorização de outros, não podendo substabelecer. O presente mandato é celebrado em caráter IRRETRATÁVEL E SEM PRESTAÇÃO DE CONTAS. O outorgante pelo presente instrumento declara-se responsável pelo pagamento de multas e impostos do veículo acima descrito e caracterizado, até a data da outorga do presente mandato.';

export const CAMPO_EM_BRANCO = '______________';

export function rotuloDocumento(valor) {
  return removerMascaraCPFCNPJ(valor || '').length > 11 ? 'CNPJ' : 'CPF';
}

// "Canguçu-RS"
export function municipioUf(p) {
  return [p.cidade, p.uf].filter(Boolean).join('-');
}

// Residência com tudo o que estiver preenchido no cadastro, para outorgante e outorgados:
// "residente na Rua Conde de Porto Alegre, Nº1365, Casa 2, bairro Centro, CEP 96600-000, no município de Canguçu-RS".
// Sem nenhum dado de endereço, deixa um espaço em branco para preencher à mão.
function residencia(p) {
  const m = municipioUf(p);
  const rua = p.logradouro ? `${p.logradouro}${p.numero ? `, Nº${p.numero}` : ''}` : (p.numero ? `Nº${p.numero}` : '');
  const cep = formatarCep(p.cep);
  const detalhes = [rua, p.complemento, p.bairro && `bairro ${p.bairro}`, cep && `CEP ${cep}`]
    .map((v) => (v || '').trim()).filter(Boolean);
  if (!detalhes.length) return `residente no município de ${m || CAMPO_EM_BRANCO}`;
  const inicio = p.logradouro ? 'residente na' : 'residente em';
  return `${inicio} ${detalhes.join(', ')}${m ? ` no município de ${m}` : ''}`;
}

// ", RG: 1234, telefone: (53) 9999-9999, e-mail: x@y.com": só o que estiver preenchido.
function dadosComplementares(p) {
  const partes = [];
  [['RG', p.rg], ['telefone', p.telefone], ['e-mail', p.email]].forEach(([rotulo, valor]) => {
    if (valor && String(valor).trim()) partes.push(`${rotulo}: ${String(valor).trim()}`);
  });
  return partes.length ? `, ${partes.join(', ')}` : '';
}

// Nome do escritório lido do JWT: reserva enquanto os dados completos (endereço e
// telefone) não chegam de /empresas/minha, ou se essa chamada falhar.
function getNomeEmpresaDoToken() {
  try {
    const token = localStorage.getItem('jwtToken');
    const decoded = token ? decodeToken(token) : null;
    return (decoded && (decoded.empresaNomeFantasia || decoded.empresaRazaoSocial)) || '';
  } catch (error) {
    return '';
  }
}

// Nome, endereço numa linha e telefone do escritório, para o cabeçalho do documento
// (identifica de qual despachante a procuração saiu, como no modelo do Wrague).
function montarCabecalhoEmpresa(e) {
  if (!e) return { nome: '', endereco: '', telefone: '' };
  // Linha 1: logradouro, número, complemento e bairro. Linha 2: cidade-UF e CEP.
  const linha1 = [];
  if (e.logradouro) {
    let rua = e.logradouro;
    if (e.numero) rua += `, ${e.numero}`;
    if (e.complemento) rua += ` - ${e.complemento}`;
    linha1.push(rua);
  }
  if (e.bairro) linha1.push(e.bairro);
  const linha2 = [];
  const cidadeUf = [e.cidade, e.uf].filter(Boolean).join('-');
  if (cidadeUf) linha2.push(cidadeUf);
  if (e.cep) linha2.push(`CEP ${e.cep}`);
  const endereco = [linha1.join(', '), linha2.join(', ')].filter(Boolean).join('\n');
  const tel = [e.ddd ? `(${e.ddd})` : '', e.telefone || ''].filter(Boolean).join(' ');
  return { nome: e.nomeFantasia || e.razaoSocial || '', endereco, telefone: tel };
}

export const escapeHtml = (v) => String(v == null ? '' : v)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// ── Modelos de procuração ──────────────────────────────────────────────────────
// VENDA é o modelo original (texto acima); os demais têm texto e campos próprios.
const TIPO_VENDA = 'VENDA';
const TIPO_AQUISICAO = 'AQUISICAO';
const TIPO_REPRESENTACAO = 'REPRESENTACAO';
const TIPO_CAUSA_PROPRIA = 'CAUSA_PROPRIA';

const TOKENS_PROCURACAO = ['OUTORGANTE', 'OUTORGADO', 'VEICULO_PLACA', 'VALOR_VENDA', 'VALOR_VENDA_EXTENSO'];

const MODELOS_PROCURACAO = [
  { tipo: TIPO_VENDA, titulo: 'Venda de Veículo', sub: 'Poderes amplos: venda, transferência, 2ª via, baixa, comunicação de venda etc.' },
  { tipo: TIPO_AQUISICAO, titulo: 'Aquisição de Veículo ("De Acordo")', sub: 'Representa o comprador e assina o "DE ACORDO" na transferência' },
  { tipo: TIPO_REPRESENTACAO, titulo: 'Representação junto ao CRVA/DETRAN', sub: 'Transferência, gravame, emplacamento, troca de placa, remarcação, remoção de depósito' },
  { tipo: TIPO_CAUSA_PROPRIA, titulo: 'Em Causa Própria', sub: 'Venda com valor, irrevogável e que não se extingue com a morte (Art. 685 do CC)' }
];

const TITULOS_DOCUMENTO = {
  [TIPO_VENDA]: 'PROCURAÇÃO',
  [TIPO_AQUISICAO]: 'PROCURAÇÃO PARA AQUISIÇÃO DE VEÍCULO',
  [TIPO_REPRESENTACAO]: 'PROCURAÇÃO PARA REPRESENTAÇÃO',
  [TIPO_CAUSA_PROPRIA]: 'PROCURAÇÃO EM CAUSA PRÓPRIA'
};

// ── Valor por extenso (R$ 25.500,00 → "vinte e cinco mil e quinhentos reais") ──
const EXT_UNIDADES = ['zero', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove', 'dez', 'onze', 'doze',
  'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
const EXT_DEZENAS = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
const EXT_CENTENAS = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];

function ateNovecentosENoventaENove(n) {
  if (n === 100) return 'cem';
  const c = Math.floor(n / 100);
  const r = n % 100;
  const partes = [];
  if (c) partes.push(EXT_CENTENAS[c]);
  if (r) {
    if (r < 20) partes.push(EXT_UNIDADES[r]);
    else {
      const d = Math.floor(r / 10);
      const u = r % 10;
      partes.push(u ? `${EXT_DEZENAS[d]} e ${EXT_UNIDADES[u]}` : EXT_DEZENAS[d]);
    }
  }
  return partes.join(' e ');
}

function inteiroPorExtenso(n) {
  if (n === 0) return 'zero';
  const blocos = [];
  let resto = n;
  [[1e9, 'bilhão', 'bilhões'], [1e6, 'milhão', 'milhões'], [1e3, 'mil', 'mil']].forEach(([v, singular, plural]) => {
    const q = Math.floor(resto / v);
    resto %= v;
    if (!q) return;
    if (v === 1e3) blocos.push({ valor: q, texto: q === 1 ? 'mil' : `${ateNovecentosENoventaENove(q)} mil` });
    else blocos.push({ valor: q, texto: `${ateNovecentosENoventaENove(q)} ${q === 1 ? singular : plural}` });
  });
  if (resto) blocos.push({ valor: resto, texto: ateNovecentosENoventaENove(resto) });
  // "e" só antes do último bloco quando ele é menor que 100 ou centena exata; senão, vírgula
  let texto = blocos[0].texto;
  for (let i = 1; i < blocos.length; i += 1) {
    const ultimo = blocos[i].valor;
    const usaE = i === blocos.length - 1 && (ultimo < 100 || ultimo % 100 === 0);
    texto += (usaE ? ' e ' : ', ') + blocos[i].texto;
  }
  return texto;
}

export function valorPorExtenso(valor) {
  const total = Math.round((Number(valor) || 0) * 100);
  const reais = Math.floor(total / 100);
  const centavos = total % 100;
  const partes = [];
  if (reais > 0 || centavos === 0) {
    const de = reais >= 1e6 && reais % 1e6 === 0 ? ' de' : '';
    partes.push(`${inteiroPorExtenso(reais)}${de} ${reais === 1 ? 'real' : 'reais'}`);
  }
  if (centavos > 0) partes.push(`${inteiroPorExtenso(centavos)} ${centavos === 1 ? 'centavo' : 'centavos'}`);
  return partes.join(' e ');
}

export function formatarMoeda(valor) {
  return (Number(valor) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// Dados do cliente → formato de pessoa usado nos formulários da procuração
export function clienteParaPessoa(cliente) {
  return {
    idCliente: cliente.idCliente,
    nome: cliente.nome || '',
    cpfCnpj: removerMascaraCPFCNPJ(cliente.cpfCnpj),
    rg: cliente.rg || '',
    telefone: cliente.telefone || '',
    email: cliente.email || '',
    cep: cliente.cep || '',
    logradouro: cliente.logradouro || '',
    numero: cliente.numero || '',
    complemento: cliente.complemento || '',
    bairro: cliente.bairro || '',
    cidade: cliente.cidade || '',
    uf: cliente.uf || ''
  };
}

// Parágrafos de poderes dos modelos novos (HTML; o texto do modelo de venda está nas constantes PODERES_*).
// Escritos na primeira pessoa, como o modelo original.
function paragrafosPoderes(tipo, { plural, valorVenda }) {
  const os = plural ? 'aos outorgados' : 'ao outorgado';
  const osSujeito = plural ? 'os outorgados' : 'o outorgado';

  if (tipo === TIPO_AQUISICAO) {
    return [
      `Confiro ${os} poderes especiais para me representar na aquisição do veículo acima descrito e caracterizado, podendo assinar, em meu nome e na qualidade de <strong>COMPRADOR</strong>, o campo <strong>“DE ACORDO”</strong> do Certificado de Registro de Veículo (CRV) / Autorização para Transferência de Propriedade de Veículo (ATPV-e) e demais documentos de transferência, reconhecer firma, assinar requerimentos, declarações de residência e de responsabilidade, prestar as informações necessárias, pagar taxas, tributos e débitos incidentes, requerer junto ao DETRAN e aos CRVAs a transferência de propriedade do veículo para o meu nome, retirar o novo CRV/CRLV-e e praticar todos os demais atos necessários ao fiel cumprimento deste mandato, não podendo substabelecer.`,
      `O presente mandato é conferido para a finalidade específica acima e extingue-se com a conclusão da transferência do veículo para o meu nome.`
    ];
  }

  if (tipo === TIPO_REPRESENTACAO) {
    return [
      `Autorizo ${osSujeito} a me representar junto ao CRVA, ao DETRAN e demais órgãos de trânsito, podendo assinar requerimentos, declaração de residência, declarações de responsabilidade e todo e qualquer documento que se faça necessário para o encaminhamento dos processos que lhe forem precisos em meu nome, em especial: <strong>transferência de propriedade</strong>, <strong>inclusão e baixa de gravame</strong>, <strong>primeiro emplacamento</strong>, <strong>troca de placa</strong> (padrão Mercosul), <strong>remarcação de chassi ou de motor</strong>, 2ª via de CRV e CRLV-e, alteração de dados e características do veículo, mudança de município, comunicação de venda, baixa do veículo, solicitação de certidões e de vistorias.`,
      `Poderá ainda solicitar e assinar termo de remoção e de liberação de veículos retidos em depósito do DETRAN, retirar o veículo, placas e documentos, pagar taxas, tributos e débitos, e praticar todos os atos necessários ao bom e fiel cumprimento deste mandato, não podendo substabelecer.`
    ];
  }

  // Em causa própria
  const valor = `<strong>${formatarMoeda(valorVenda)} (${valorPorExtenso(valorVenda)})</strong>`;
  return [
    `Pelo presente instrumento, na qualidade de legítimo proprietário do veículo acima descrito e caracterizado, confiro ${os} poderes <strong>EM CAUSA PRÓPRIA</strong>, nos termos do Art. 685 do Código Civil, para vender, ceder e transferir o referido veículo para si próprio ou para quem lhe convier, pelo preço certo e ajustado de ${valor}, valor que declaro ter recebido integralmente, dando plena, geral e irrevogável quitação, para nada mais reclamar, a qualquer título.`,
    `Ficam conferidos poderes para assinar, em meu nome, o CRV/ATPV-e, requerimentos, declarações de residência e de responsabilidade e a comunicação de venda (Art. 134 do Código de Trânsito Brasileiro), requerer a transferência de propriedade (Art. 123 do CTB), 2ª via de documentos, baixa e inclusão de gravame, troca de município, reconhecer firma, pagar taxas, retirar o veículo e documentos, e praticar todos os atos necessários, inclusive junto ao DETRAN e aos CRVAs, <strong>podendo transferir para si próprio</strong>, o que autorizo expressamente (Art. 117 do Código Civil).`,
    `O presente mandato é conferido em caráter <strong>IRREVOGÁVEL E IRRETRATÁVEL</strong>, no interesse ${plural ? 'dos outorgados' : 'do outorgado'} e como condição do negócio de compra e venda, sendo ineficaz qualquer revogação (Arts. 684 e 685 do Código Civil), sem prestação de contas, e <strong>NÃO SE EXTINGUE PELA MORTE OU INTERDIÇÃO</strong> de qualquer das partes, obrigando herdeiros e sucessores (Art. 685 do Código Civil). ${plural ? 'Os outorgados assumem' : 'O outorgado assume'}, a partir desta data, a posse, o uso e a responsabilidade civil, criminal, administrativa e tributária pelo veículo, inclusive multas, débitos e demais penalidades posteriores a este instrumento.`
  ];
}

const UNIDADES_VALIDADE = { DIAS: ['dia', 'dias'], MESES: ['mês', 'meses'], ANOS: ['ano', 'anos'] };

// "90 (noventa) dias, a contar da data de sua assinatura"
function textoValidade({ quantidade, unidade }) {
  const n = Number(quantidade);
  const [singular, plural] = UNIDADES_VALIDADE[unidade] || UNIDADES_VALIDADE.DIAS;
  return `${n} (${inteiroPorExtenso(n)}) ${n === 1 ? singular : plural}, a contar da data de sua assinatura`;
}

// HTML do documento no formato do modelo. É gerado uma vez ao clicar em "Gerar Procuração" e
// entregue a um editor contentEditable: a partir daí o texto é do usuário (estilo Word) e o React
// não o redesenha, então edições pontuais não se perdem.
// Substitui {{TOKEN}} pelo valor correspondente. Token sem valor no mapa vira string vazia —
// mesmo esquema simples usado no backend para os textos de Contrato.
function substituirTokens(texto, valores) {
  let resultado = texto;
  Object.entries(valores).forEach(([chave, valor]) => {
    resultado = resultado.split(`{{${chave}}}`).join(escapeHtml(valor || '')); // dado de cliente/veículo entra sempre escapado
  });
  return resultado;
}

function montarDocumentoHtml({ tipo = TIPO_VENDA, valorVenda = null, validade = null, empresa, outorgante, nacionalidade, outorgados, veiculo, dataTexto, textoCustomizado = null }) {
  const e = escapeHtml;
  const cab = [];
  if (empresa.nome) cab.push(`<p>${e(empresa.nome)}</p>`);
  if (empresa.endereco) empresa.endereco.split('\n').forEach((l) => cab.push(`<p>${e(l)}</p>`));
  if (empresa.telefone) cab.push(`<p>FONES: ${e(empresa.telefone)}</p>`);
  const cabecalho = cab.length ? `<div class="cab">${cab.join('')}</div>` : '';

  const nac = nacionalidade.trim() ? `, ${e(nacionalidade.trim())}` : '';
  const outorgadosHtml = outorgados.map((o, i) =>
    `${i > 0 ? ' e/ou ' : ''}<strong>${e(o.nome)}</strong>, ${e(rotuloDocumento(o.cpfCnpj))}: <strong>${e(formatarCpfCnpj(o.cpfCnpj))}</strong>${e(dadosComplementares(o))}, ${e(residencia(o))}`
  ).join('');

  // Na representação o veículo é opcional: só aparece se algum dado foi preenchido
  const temVeiculo = tipo !== TIPO_REPRESENTACAO ||
    [veiculo.placa, veiculo.chassi, veiculo.renavam, veiculo.marcaModelo, veiculo.cor].some((v) => v && String(v).trim());
  const linhasVeiculo = [];
  const linha = (rotulo, valor) => linhasVeiculo.push(`<div><strong>${rotulo}:</strong> ${e(valor)}</div>`);
  if (tipo === TIPO_REPRESENTACAO) {
    if (veiculo.placa) linha('PLACA', veiculo.placa.toUpperCase());
    if (veiculo.chassi) linha('CHASSI', veiculo.chassi.toUpperCase());
    if (veiculo.renavam) linha('RENAVAM', veiculo.renavam);
    if (veiculo.marcaModelo) linha('MARCA', veiculo.marcaModelo);
    if (veiculo.cor) linha('COR', veiculo.cor);
  } else {
    linha('PLACA', (veiculo.placa || '').toUpperCase());
    linha('CHASSI', (veiculo.chassi || '').toUpperCase());
    linha('RENAVAM', veiculo.renavam);
    linha('MARCA', veiculo.marcaModelo);
    linha('COR', veiculo.cor);
  }

  let poderes;
  if (textoCustomizado) {
    const tokens = {
      OUTORGANTE: outorgante.nome || '',
      OUTORGADO: outorgados.map((o) => o.nome).filter(Boolean).join(' e/ou '),
      VEICULO_PLACA: (veiculo.placa || '').toUpperCase(),
      VALOR_VENDA: valorVenda != null ? formatarMoeda(valorVenda) : '',
      VALOR_VENDA_EXTENSO: valorVenda != null ? valorPorExtenso(valorVenda) : ''
    };
    poderes = substituirTokens(textoCustomizado, tokens).split('\n\n').map((p) => `<p class="par">${p}</p>`).join('\n');
  } else {
    poderes = tipo === TIPO_VENDA
      ? `<p class="par">${e(PODERES_ANTES)}<strong>${e(PODERES_NEGRITO)}</strong>${e(PODERES_DEPOIS)}</p>`
      : paragrafosPoderes(tipo, { plural: outorgados.length > 1, valorVenda }).map((p) => `<p class="par">${p}</p>`).join('\n');
  }

  return `${cabecalho}
<h1>${TITULOS_DOCUMENTO[tipo] || TITULOS_DOCUMENTO[TIPO_VENDA]}</h1>
<p class="par">OUTORGANTE: <strong>${e(outorgante.nome)}</strong>${nac}, ${e(rotuloDocumento(outorgante.cpfCnpj))} <strong>${e(formatarCpfCnpj(outorgante.cpfCnpj))}</strong>${e(dadosComplementares(outorgante))}, ${e(residencia(outorgante))}, nomeio e constituo como meu procurador.</p>
<p class="par">OUTORGADO (a): ${outorgadosHtml}${temVeiculo ? '<br>REFERENTE AO VEÍCULO:' : ''}</p>
${temVeiculo ? `<div class="veiculo">\n${linhasVeiculo.join('\n')}\n</div>\n` : ''}${poderes}
${validade ? `<p class="par"><strong>PRAZO DE VALIDADE:</strong> a presente procuração tem validade de ${textoValidade(validade)}.</p>\n` : ''}<div class="fecha">
<p class="data">DATA ${e(dataTexto)}</p>
<div class="linha"></div>
<span>ASSINATURA E RECONHECER FIRMA (POR VERDADEIRA)</span>
</div>`;
}

// ─── Bloco de endereço reutilizável (outorgante e cada outorgado) ────────────

export const EnderecoCampos = ({ valor, onChange, onCepChange, buscando }) => (
  <>
    <FormRow>
      <InputGroup>
        <Label>CEP</Label>
        <Input
          value={valor.cep}
          onChange={(e) => onCepChange(e.target.value)}
          placeholder="00000-000"
        />
        {buscando && <HintText>Buscando endereço...</HintText>}
      </InputGroup>
      <InputGroup style={{ flex: 2 }}>
        <Label>Cidade</Label>
        <Input value={valor.cidade} onChange={(e) => onChange('cidade', e.target.value)} placeholder="Canguçu" />
      </InputGroup>
      <InputGroup style={{ flex: '0 0 70px' }}>
        <Label>UF</Label>
        <Input
          value={valor.uf}
          onChange={(e) => onChange('uf', e.target.value.toUpperCase())}
          placeholder="RS"
          maxLength={2}
          style={{ textTransform: 'uppercase' }}
        />
      </InputGroup>
    </FormRow>
    <FormRow>
      <InputGroup style={{ flex: 2 }}>
        <Label>Logradouro</Label>
        <Input value={valor.logradouro} onChange={(e) => onChange('logradouro', e.target.value)} placeholder="Rua Joaquim Paulo de Freitas" />
      </InputGroup>
      <InputGroup>
        <Label>Número</Label>
        <Input value={valor.numero} onChange={(e) => onChange('numero', e.target.value)} placeholder="140" />
      </InputGroup>
    </FormRow>
    <FormRow>
      <InputGroup>
        <Label>Complemento</Label>
        <Input value={valor.complemento} onChange={(e) => onChange('complemento', e.target.value)} placeholder="Casa, apto, bloco..." />
      </InputGroup>
      <InputGroup>
        <Label>Bairro</Label>
        <Input value={valor.bairro} onChange={(e) => onChange('bairro', e.target.value)} placeholder="Triângulo" />
      </InputGroup>
    </FormRow>
  </>
);

// ─── Componente ───────────────────────────────────────────────────────────────

const Procuracao = () => {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();
  const previewRef = useRef(null);

  const [outorgante, setOutorgante] = useState(pessoaVazia());
  const [importacaoOutorgante, setImportacaoOutorgante] = useState(null); // documento importado do outorgante (a ser anexado ao salvar)
  const [salvandoOutorgante, setSalvandoOutorgante] = useState(false);
  const [veiculosOutorgante, setVeiculosOutorgante] = useState([]);
  const [veiculo, setVeiculo] = useState(veiculoVazio());
  const [importacaoVeiculo, setImportacaoVeiculo] = useState(null); // CRLV/ATPV importado (a ser anexado ao veículo ao salvar)
  const [salvandoVeiculo, setSalvandoVeiculo] = useState(false);
  const [outorgados, setOutorgados] = useState([pessoaVazia()]);
  const [salvandoOutorgadoIndex, setSalvandoOutorgadoIndex] = useState(null);
  // Snapshot (formato clienteParaPessoa/veiculoDoCadastro) do que está gravado no cadastro,
  // pra comparar com o que está preenchido aqui e mostrar a situação (novo/alterado/igual).
  const [origem, setOrigem] = useState({ outorgante: null, outorgados: [null], veiculo: null });
  const [confirmacaoCadastro, setConfirmacaoCadastro] = useState(null);
  // Nacionalidade do outorgante: aparece no documento como no modelo ("brasileiro"); apagar o campo omite a palavra
  const [nacionalidadeOutorgante, setNacionalidadeOutorgante] = useState('brasileiro');
  const [dadosEmpresaCabecalho, setDadosEmpresaCabecalho] = useState(
    () => ({ nome: getNomeEmpresaDoToken(), endereco: '', telefone: '' })
  );
  // Documento em edição: HTML inicial (snapshot) e "versão" que força o editor a ser refeito
  // quando a procuração é gerada de novo ou restaurada ao texto original.
  const editorRef = useRef(null);
  const [documentoHtml, setDocumentoHtml] = useState('');
  const [versaoDocumento, setVersaoDocumento] = useState(0);
  const editadoRef = useRef(false);
  // Modelo de procuração escolhido e valor da venda (só usado em "Em Causa Própria")
  const [tipoProcuracao, setTipoProcuracao] = useState(TIPO_VENDA);
  const [valorVenda, setValorVenda] = useState(null);
  const [documentoCompacto, setDocumentoCompacto] = useState(false);
  // Prazo de validade opcional, descrito ao final do texto de qualquer modelo
  const [usarValidade, setUsarValidade] = useState(false);
  const [validadeQtd, setValidadeQtd] = useState('');
  const [validadeUnidade, setValidadeUnidade] = useState('DIAS');
  const [erroValidade, setErroValidade] = useState('');
  const [erroValorVenda, setErroValorVenda] = useState('');

  // Grupos de outorgados (ex.: "Loja Central Car") — importar/salvar conjuntos de uma vez
  const [grupos, setGrupos] = useState([]);
  const [modalGrupos, setModalGrupos] = useState(null); // null | 'importar' | 'salvar'
  const [nomeGrupo, setNomeGrupo] = useState('');
  const [processandoGrupo, setProcessandoGrupo] = useState(false);
  // Data opcional: em branco imprime "DATA / /" para preencher à mão, como no modelo
  const [dataDocumento, setDataDocumento] = useState('');

  const [mostrarPreview, setMostrarPreview] = useState(false);

  // Textos de "poderes" customizados pela empresa em Configurações, por tipo de procuração.
  // Ausência de entrada para um tipo significa "usa o texto padrão do sistema".
  const [textosCustomizados, setTextosCustomizados] = useState({});
  const [modalConfigAberto, setModalConfigAberto] = useState(false);

  const carregarTextosCustomizados = async () => {
    try {
      const resp = await axios.get(`${API_BASE_URL}/modelos-documento-texto`, {
        ...getAuthHeader(),
        params: { categoria: 'PROCURACAO' }
      });
      const mapa = {};
      (Array.isArray(resp.data) ? resp.data : []).forEach((item) => { mapa[item.modelo] = item.texto; });
      setTextosCustomizados(mapa);
    } catch (error) {
      console.error('Erro ao carregar textos customizados da procuração:', error);
    }
  };

  useEffect(() => {
    carregarTextosCustomizados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Texto padrão de fábrica (com dados de exemplo) — ponto de partida do editor em Configurações
  const buscarTextoPadraoProcuracao = async (tipo) => {
    if (tipo === TIPO_VENDA) {
      return `${PODERES_ANTES}${PODERES_NEGRITO}${PODERES_DEPOIS}`;
    }
    return paragrafosPoderes(tipo, { plural: false, valorVenda: 10000 }).join('\n\n');
  };

  // Erros de validação por campo — mostrados junto ao campo, não só em toast
  const [errosOutorgante, setErrosOutorgante] = useState({});
  const [errosVeiculo, setErrosVeiculo] = useState({});
  const [errosOutorgados, setErrosOutorgados] = useState([]);

  // Busca de cliente (reaproveitada para outorgante e outorgados)
  const [modalBuscaAberto, setModalBuscaAberto] = useState(false);
  const [buscaAlvo, setBuscaAlvo] = useState(null); // 'outorgante' | índice numérico do outorgado
  const [termoBusca, setTermoBusca] = useState('');
  const [resultadosBusca, setResultadosBusca] = useState([]);
  const [buscando, setBuscando] = useState(false);

  // Busca automática de CEP
  const [buscandoCepOutorgante, setBuscandoCepOutorgante] = useState(false);
  const [buscandoCepOutorgadoIndex, setBuscandoCepOutorgadoIndex] = useState(null);

  // Busca/seleção de veículo (do outorgante ou por placa, em qualquer cadastro)
  const [modalVeiculoAberto, setModalVeiculoAberto] = useState(false);
  const [placaBusca, setPlacaBusca] = useState('');
  const [buscandoPlaca, setBuscandoPlaca] = useState(false);
  const [resultadoPlaca, setResultadoPlaca] = useState(null);
  const [erroPlaca, setErroPlaca] = useState('');

  const getAuthHeader = () => {
    const token = localStorage.getItem('jwtToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // Endereço e telefone do escritório para o cabeçalho impresso
  useEffect(() => {
    let ativo = true;
    axios.get(`${API_BASE_URL}/empresas/minha`, getAuthHeader())
      .then((resp) => {
        if (!ativo || !resp || !resp.data) return;
        const dados = montarCabecalhoEmpresa(resp.data);
        setDadosEmpresaCabecalho((atual) => ({ ...dados, nome: dados.nome || atual.nome }));
      })
      .catch(() => { /* mantém só o nome do token */ });
    return () => { ativo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Busca de clientes cadastrados (outorgante / outorgados) ──────────────
  const abrirBusca = (alvo) => {
    setBuscaAlvo(alvo);
    setTermoBusca('');
    setResultadosBusca([]);
    setModalBuscaAberto(true);
  };

  const executarBusca = async () => {
    const termo = termoBusca.trim();
    if (termo.length < 3) {
      addToast('warning', 'Informe ao menos 3 caracteres para pesquisar.');
      return;
    }
    setBuscando(true);
    setResultadosBusca([]);

    try {
      try {
        const respCpf = await axios.get(
          `${API_BASE_URL}/clientes/cpfcnpj/${removerMascaraCPFCNPJ(termo)}`,
          getAuthHeader()
        );
        if (respCpf.data) {
          setResultadosBusca([respCpf.data]);
          return;
        }
      } catch (err) {
        // não encontrou por CPF/CNPJ, tenta por nome
      }

      const respNome = await axios.get(
        `${API_BASE_URL}/clientes/nome/${termo.toLowerCase()}`,
        getAuthHeader()
      );
      if (respNome.data && respNome.data.length > 0) {
        setResultadosBusca(respNome.data);
      }
    } catch (error) {
      console.error('Erro na pesquisa de clientes:', error);
      addToast('error', 'Erro ao pesquisar clientes.');
    } finally {
      setBuscando(false);
    }
  };

  const selecionarResultado = async (clienteResumo) => {
    setBuscando(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/clientes/${clienteResumo.idCliente}`,
        getAuthHeader()
      );
      const cliente = response.data;

      const dadosPessoa = clienteParaPessoa(cliente);

      if (buscaAlvo === 'outorgante') {
        setOutorgante(dadosPessoa);
        setOrigem((o) => ({ ...o, outorgante: dadosPessoa }));
        setVeiculosOutorgante(Array.isArray(cliente.veiculos) ? cliente.veiculos : []);
        setVeiculo(veiculoVazio());
        setImportacaoVeiculo(null);
        setOrigem((o) => ({ ...o, veiculo: null }));
      } else {
        setOutorgados((prev) =>
          prev.map((o, i) => (i === buscaAlvo ? dadosPessoa : o))
        );
        setOrigem((o) => ({
          ...o,
          outorgados: o.outorgados.map((c, i) => (i === buscaAlvo ? dadosPessoa : c))
        }));
      }

      setModalBuscaAberto(false);
    } catch (error) {
      console.error('Erro ao carregar cliente:', error);
      addToast('error', 'Erro ao carregar dados do cliente.');
    } finally {
      setBuscando(false);
    }
  };

  // ── Outorgante (campos manuais, caso não queira usar a busca) ────────────
  const handleOutorganteChange = (e) => {
    const { name, value } = e.target;
    setOutorgante((prev) => ({ ...prev, [name]: value }));
  };

  const handleOutorganteCepChange = async (valor) => {
    const formatado = formatarCep(valor);
    setOutorgante((prev) => ({ ...prev, cep: formatado }));
    if (formatado.replace(/\D/g, '').length === 8) {
      setBuscandoCepOutorgante(true);
      const dados = await buscarCep(formatado);
      if (dados) setOutorgante((prev) => ({ ...prev, ...dados }));
      setBuscandoCepOutorgante(false);
    }
  };

  // ── Veículo ────────────────────────────────────────────────────────────
  const handleVeiculoChange = (e) => {
    const { name, value } = e.target;
    setVeiculo((prev) => ({ ...prev, [name]: value }));
  };

  const abrirModalVeiculo = () => {
    setPlacaBusca('');
    setResultadoPlaca(null);
    setErroPlaca('');
    setModalVeiculoAberto(true);
  };

  const buscarVeiculoPorPlaca = async () => {
    const placa = placaBusca.trim().toUpperCase();
    if (!placa) return;
    setBuscandoPlaca(true);
    setResultadoPlaca(null);
    setErroPlaca('');
    try {
      const resp = await axios.get(`${API_BASE_URL}/veiculos/placa/${placa}`, getAuthHeader());
      setResultadoPlaca(resp.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setErroPlaca('Nenhum veículo cadastrado com essa placa.');
      } else {
        console.error('Erro ao buscar veículo por placa:', error);
        setErroPlaca('Erro ao buscar veículo. Tente novamente.');
      }
    } finally {
      setBuscandoPlaca(false);
    }
  };

  const importarVeiculo = (v) => {
    const dados = {
      idVeiculo: v.idVeiculo,
      marcaModelo: v.marcaModelo || '',
      cor: v.cor || '',
      placa: v.placa || '',
      chassi: v.chassi || '',
      renavam: v.renavam || '',
      ano: v.ano || ''
    };
    setVeiculo(dados);
    setImportacaoVeiculo(null);
    setOrigem((o) => ({ ...o, veiculo: dados }));
    setModalVeiculoAberto(false);
    addToast('success', 'Dados do veículo importados.');
  };

  // ── Outorgados ─────────────────────────────────────────────────────────
  const adicionarOutorgado = () => {
    setOutorgados((prev) => [...prev, pessoaVazia()]);
    setOrigem((o) => ({ ...o, outorgados: [...o.outorgados, null] }));
  };

  const removerOutorgado = (index) => {
    setOutorgados((prev) => prev.filter((_, i) => i !== index));
    setErrosOutorgados((prev) => prev.filter((_, i) => i !== index));
    setOrigem((o) => ({ ...o, outorgados: o.outorgados.filter((_, i) => i !== index) }));
  };

  const atualizarOutorgado = (index, field, value) => {
    setOutorgados((prev) =>
      prev.map((o, i) => (i === index ? { ...o, [field]: value } : o))
    );
  };

  // ── Importar dados de CNH/RG e guardar o arquivo no cadastro do cliente ──
  const anexarImportacao = async (idCliente, importacao, cpfSalvo) => {
    const { arquivo, divergente } = resolverAnexo(importacao, cpfSalvo);
    if (divergente) {
      addToast('warning', 'O documento importado é de outro CPF/CNPJ; não foi anexado a este cliente. Anexe-o pelos documentos do cliente, se for o caso.');
      return;
    }
    if (!arquivo) return;
    const anexou = await anexarDocumentoCliente(API_BASE_URL, idCliente, arquivo, getAuthHeader());
    addToast(
      anexou ? 'success' : 'warning',
      anexou ? 'Documento guardado nos documentos do cliente.' : 'Cadastro salvo, mas não foi possível guardar o documento. Anexe-o pelos documentos do cliente.'
    );
  };


  // ── Importar dados do CRLV/ATPV e guardar o arquivo no cadastro do veículo ──
  const anexarImportacaoVeiculo = async (idVeiculo, placaSalva) => {
    const { arquivo, divergente } = resolverAnexo(importacaoVeiculo, placaSalva);
    setImportacaoVeiculo(null);
    if (divergente) {
      addToast('warning', 'O documento importado é de outra placa; não foi anexado a este veículo. Anexe-o pelos documentos do veículo, se for o caso.');
      return;
    }
    if (!arquivo) return;
    const anexou = await anexarDocumentoVeiculo(API_BASE_URL, idVeiculo, arquivo, getAuthHeader());
    addToast(
      anexou ? 'success' : 'warning',
      anexou ? 'Documento guardado nos documentos do veículo.' : 'Veículo salvo, mas não foi possível guardar o documento. Anexe-o pelos documentos do veículo.'
    );
  };

  const aplicarDocumentoVeiculo = (dados) => setVeiculo((prev) => ({ ...prev, ...dados }));
  const aplicarDocumentoOutorgante = (dados) => setOutorgante((prev) => ({ ...prev, ...dados }));
  const aplicarDocumentoOutorgado = (index, dados) => {
    setOutorgados((prev) => prev.map((o, i) => (i === index ? { ...o, ...dados } : o)));
  };

  const handleOutorgadoCepChange = async (index, valor) => {
    const formatado = formatarCep(valor);
    atualizarOutorgado(index, 'cep', formatado);
    if (formatado.replace(/\D/g, '').length === 8) {
      setBuscandoCepOutorgadoIndex(index);
      const dados = await buscarCep(formatado);
      if (dados) {
        setOutorgados((prev) => prev.map((o, i) => (i === index ? { ...o, ...dados } : o)));
      }
      setBuscandoCepOutorgadoIndex(null);
    }
  };

  // ── Salvar/atualizar o cadastro de cliente a partir dos dados preenchidos aqui ──
  // Se já tem idCliente, atualiza só os campos preenchidos (nunca apaga o que já está no
  // cadastro); senão, verifica se já existe um cliente com o mesmo CPF/CNPJ (evita duplicar)
  // e cria um novo caso não exista.
  const payloadPessoa = (pessoa) => ({
    nome: pessoa.nome, cpfCnpj: pessoa.cpfCnpj.replace(/\D/g, ''), rg: pessoa.rg, telefone: pessoa.telefone, email: pessoa.email,
    cep: pessoa.cep, logradouro: pessoa.logradouro, numero: pessoa.numero, complemento: pessoa.complemento,
    bairro: pessoa.bairro, cidade: pessoa.cidade, uf: pessoa.uf
  });

  const complementarClienteOutorgante = async (idCliente) => {
    setSalvandoOutorgante(true);
    try {
      const response = await axios.put(`${API_BASE_URL}/clientes/${idCliente}/complementar`, payloadPessoa(outorgante), getAuthHeader());
      const dados = clienteParaPessoa(response.data);
      setOutorgante(dados);
      setOrigem((o) => ({ ...o, outorgante: dados }));
      addToast('success', 'Cadastro do outorgante atualizado com sucesso!');
      await anexarImportacao(idCliente, importacaoOutorgante, response.data.cpfCnpj);
      setImportacaoOutorgante(null);
    } catch (error) {
      console.error('Erro ao atualizar cliente (outorgante):', error);
      addToast('error', 'Erro ao salvar dados do cliente: ' + (error.response?.data?.message || error.message));
    } finally {
      setSalvandoOutorgante(false);
    }
  };

  const salvarClienteOutorgante = async () => {
    if (!outorgante.nome || !outorgante.cpfCnpj) {
      addToast('warning', 'Preencha ao menos nome e CPF/CNPJ antes de salvar.');
      return;
    }
    if (outorgante.idCliente) {
      await complementarClienteOutorgante(outorgante.idCliente);
      return;
    }
    setSalvandoOutorgante(true);
    try {
      const existente = await buscarClientePorDocumento(outorgante.cpfCnpj);
      if (existente) {
        setSalvandoOutorgante(false);
        setConfirmacaoCadastro({
          titulo: 'Cliente já cadastrado',
          mensagem: `Já existe um cliente com este CPF/CNPJ: ${existente.nome}. Atualizar o cadastro dele com os dados preenchidos aqui?`,
          textoConfirmar: 'Atualizar',
          onConfirm: () => complementarClienteOutorgante(existente.idCliente)
        });
        return;
      }
      const response = await axios.post(`${API_BASE_URL}/clientes`, { ...payloadPessoa(outorgante), ativo: true }, getAuthHeader());
      const dados = clienteParaPessoa(response.data);
      setOutorgante(dados);
      setOrigem((o) => ({ ...o, outorgante: dados }));
      addToast('success', 'Outorgante cadastrado com sucesso!');
      await anexarImportacao(response.data.idCliente, importacaoOutorgante, response.data.cpfCnpj);
      setImportacaoOutorgante(null);
    } catch (error) {
      console.error('Erro ao cadastrar cliente (outorgante):', error);
      addToast('error', 'Erro ao salvar dados do cliente: ' + (error.response?.data?.message || error.message));
    } finally {
      setSalvandoOutorgante(false);
    }
  };

  const complementarClienteOutorgado = async (index, idCliente) => {
    setSalvandoOutorgadoIndex(index);
    try {
      const response = await axios.put(`${API_BASE_URL}/clientes/${idCliente}/complementar`, payloadPessoa(outorgados[index]), getAuthHeader());
      const dados = clienteParaPessoa(response.data);
      setOutorgados((prev) => prev.map((item, i) => (i === index ? dados : item)));
      setOrigem((o) => ({ ...o, outorgados: o.outorgados.map((c, i) => (i === index ? dados : c)) }));
      addToast('success', 'Cadastro do outorgado atualizado com sucesso!');
      await anexarImportacao(idCliente, outorgados[index]._importacao, response.data.cpfCnpj);
    } catch (error) {
      console.error('Erro ao atualizar cliente (outorgado):', error);
      addToast('error', 'Erro ao salvar dados do cliente: ' + (error.response?.data?.message || error.message));
    } finally {
      setSalvandoOutorgadoIndex(null);
    }
  };

  const salvarClienteOutorgado = async (index) => {
    const o = outorgados[index];
    if (!o.nome || !o.cpfCnpj) {
      addToast('warning', 'Preencha ao menos nome e CPF/CNPJ antes de salvar.');
      return;
    }
    if (o.idCliente) {
      await complementarClienteOutorgado(index, o.idCliente);
      return;
    }
    setSalvandoOutorgadoIndex(index);
    try {
      const existente = await buscarClientePorDocumento(o.cpfCnpj);
      if (existente) {
        setSalvandoOutorgadoIndex(null);
        setConfirmacaoCadastro({
          titulo: 'Cliente já cadastrado',
          mensagem: `Já existe um cliente com este CPF/CNPJ: ${existente.nome}. Atualizar o cadastro dele com os dados preenchidos aqui?`,
          textoConfirmar: 'Atualizar',
          onConfirm: () => complementarClienteOutorgado(index, existente.idCliente)
        });
        return;
      }
      const response = await axios.post(`${API_BASE_URL}/clientes`, { ...payloadPessoa(o), ativo: true }, getAuthHeader());
      const dados = clienteParaPessoa(response.data);
      setOutorgados((prev) => prev.map((item, i) => (i === index ? dados : item)));
      setOrigem((org) => ({ ...org, outorgados: org.outorgados.map((c, i) => (i === index ? dados : c)) }));
      addToast('success', 'Outorgado cadastrado com sucesso!');
      await anexarImportacao(response.data.idCliente, o._importacao, response.data.cpfCnpj);
    } catch (error) {
      console.error('Erro ao cadastrar cliente (outorgado):', error);
      addToast('error', 'Erro ao salvar dados do cliente: ' + (error.response?.data?.message || error.message));
    } finally {
      setSalvandoOutorgadoIndex(null);
    }
  };

  // ── Salvar/atualizar o cadastro de veículo a partir dos dados preenchidos aqui ──
  // O veículo fica vinculado ao outorgante: por isso exige o outorgante já salvo no cadastro.
  const payloadVeiculo = () => ({
    placa: veiculo.placa.trim().toUpperCase(), chassi: veiculo.chassi, renavam: veiculo.renavam,
    marcaModelo: veiculo.marcaModelo, ano: veiculo.ano, cor: veiculo.cor
  });

  const aplicarVeiculoSalvo = (dadosVeiculo) => {
    const dados = {
      idVeiculo: dadosVeiculo.idVeiculo, marcaModelo: dadosVeiculo.marcaModelo || '', cor: dadosVeiculo.cor || '',
      placa: dadosVeiculo.placa || '', chassi: dadosVeiculo.chassi || '', renavam: dadosVeiculo.renavam || '', ano: dadosVeiculo.ano || ''
    };
    setVeiculo(dados);
    setOrigem((o) => ({ ...o, veiculo: dados }));
    if (outorgante.idCliente) {
      axios.get(`${API_BASE_URL}/clientes/${outorgante.idCliente}`, getAuthHeader())
        .then((r) => setVeiculosOutorgante(Array.isArray(r.data.veiculos) ? r.data.veiculos : []))
        .catch(() => {});
    }
  };

  const complementarVeiculoNoCadastro = async (idVeiculo) => {
    setSalvandoVeiculo(true);
    try {
      const response = await axios.put(`${API_BASE_URL}/veiculos/${idVeiculo}/complementar`, payloadVeiculo(), getAuthHeader());
      aplicarVeiculoSalvo(response.data);
      addToast('success', 'Cadastro do veículo atualizado com sucesso!');
      await anexarImportacaoVeiculo(response.data.idVeiculo || idVeiculo, response.data.placa);
    } catch (error) {
      console.error('Erro ao atualizar veículo:', error);
      addToast('error', 'Erro ao salvar dados do veículo: ' + (error.response?.data?.message || error.message));
    } finally {
      setSalvandoVeiculo(false);
    }
  };

  const salvarVeiculoNoCadastro = async () => {
    const placa = veiculo.placa.trim();
    if (!placa || !veiculo.marcaModelo.trim()) {
      addToast('warning', 'Informe ao menos a placa e a marca/modelo para salvar no cadastro.');
      return;
    }
    if (veiculo.idVeiculo) {
      await complementarVeiculoNoCadastro(veiculo.idVeiculo);
      return;
    }
    setSalvandoVeiculo(true);
    try {
      const existente = await buscarVeiculoCadastradoPorPlaca(placa);
      if (existente) {
        setSalvandoVeiculo(false);
        setConfirmacaoCadastro({
          titulo: 'Veículo já cadastrado',
          mensagem: `Já existe um veículo com esta placa: ${existente.marcaModelo || existente.placa}${existente.nomeCliente ? ` (cliente ${existente.nomeCliente})` : ''}. Atualizar o cadastro dele com os dados preenchidos aqui?`,
          textoConfirmar: 'Atualizar',
          onConfirm: () => complementarVeiculoNoCadastro(existente.idVeiculo)
        });
        return;
      }
      if (!outorgante.idCliente) {
        addToast('warning', 'Salve primeiro os dados do outorgante no cadastro: o veículo fica vinculado a ele.');
        return;
      }
      const response = await axios.post(`${API_BASE_URL}/veiculos`, { ...payloadVeiculo(), cliente: { idCliente: outorgante.idCliente } }, getAuthHeader());
      aplicarVeiculoSalvo(response.data);
      addToast('success', 'Veículo cadastrado com sucesso!');
      await anexarImportacaoVeiculo(response.data.idVeiculo, response.data.placa);
    } catch (error) {
      console.error('Erro ao cadastrar veículo:', error);
      addToast('error', 'Erro ao salvar dados do veículo: ' + (error.response?.data?.message || error.message));
    } finally {
      setSalvandoVeiculo(false);
    }
  };

  // ── Grupos de outorgados ───────────────────────────────────────────────────
  const carregarGrupos = async () => {
    try {
      const resp = await axios.get(`${API_BASE_URL}/grupos-outorgados`, getAuthHeader());
      setGrupos(Array.isArray(resp.data) ? resp.data : []);
    } catch (error) {
      console.error('Erro ao carregar grupos de outorgados:', error);
    }
  };

  useEffect(() => {
    carregarGrupos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const outorgadosPreenchidos = () => outorgados.filter((o) => o.nome || o.cpfCnpj);

  const abrirModalSalvarGrupo = () => {
    if (outorgadosPreenchidos().length === 0) {
      addToast('warning', 'Adicione ao menos um outorgado antes de salvar o grupo.');
      return;
    }
    setNomeGrupo('');
    setModalGrupos('salvar');
  };

  // Importa todos os membros do grupo de uma vez, com os dados atuais de cada cliente.
  // Blocos de outorgado em branco são substituídos; os já preenchidos ficam e o grupo é somado
  // (sem repetir quem já está na lista).
  const importarGrupo = async (grupo) => {
    setProcessandoGrupo(true);
    try {
      const respostas = await Promise.all(
        grupo.idsClientes.map((id) =>
          axios.get(`${API_BASE_URL}/clientes/${id}`, getAuthHeader()).then((r) => r.data).catch(() => null)
        )
      );
      const clientes = respostas.filter(Boolean);
      if (clientes.length === 0) {
        addToast('error', 'Nenhum cliente deste grupo foi encontrado.');
        return;
      }
      if (clientes.length < respostas.length) {
        addToast('warning', `${respostas.length - clientes.length} cliente(s) do grupo não existem mais e não foram importados.`);
      }

      const mantidos = outorgados
        .map((o, i) => ({ pessoa: o, base: origem.outorgados[i] || null }))
        .filter((x) => x.pessoa.nome || x.pessoa.cpfCnpj);
      const novos = clientes.filter((c) => !mantidos.some((x) => x.pessoa.idCliente === c.idCliente));
      const novosDados = novos.map(clienteParaPessoa);

      setOutorgados([...mantidos.map((x) => x.pessoa), ...novosDados]);
      setOrigem((o) => ({ ...o, outorgados: [...mantidos.map((x) => x.base), ...novosDados] }));
      setErrosOutorgados([]);
      setModalGrupos(null);
      addToast('success', `Grupo "${grupo.nome}" importado (${novos.length} outorgado(s) adicionados).`);
    } finally {
      setProcessandoGrupo(false);
    }
  };

  const salvarGrupo = async () => {
    const nome = nomeGrupo.trim();
    if (!nome) {
      addToast('warning', 'Informe o nome do grupo.');
      return;
    }
    const membros = outorgadosPreenchidos();
    const semCadastro = membros.filter((o) => !o.idCliente);
    if (semCadastro.length > 0) {
      addToast('warning', `Salve no cadastro de clientes antes (botão "Salvar no Cadastro de Clientes"): ${semCadastro.map((o) => o.nome || o.cpfCnpj).join(', ')}.`);
      return;
    }
    const existente = grupos.find((g) => g.nome.toLowerCase() === nome.toLowerCase());
    if (existente && !window.confirm(`Já existe o grupo "${existente.nome}". Substituir os outorgados dele por estes ${membros.length}?`)) {
      return;
    }

    setProcessandoGrupo(true);
    try {
      const payload = { nome, idsClientes: membros.map((o) => o.idCliente) };
      if (existente) await axios.put(`${API_BASE_URL}/grupos-outorgados/${existente.idGrupo}`, payload, getAuthHeader());
      else await axios.post(`${API_BASE_URL}/grupos-outorgados`, payload, getAuthHeader());
      await carregarGrupos();
      setModalGrupos(null);
      addToast('success', `Grupo "${nome}" salvo com ${membros.length} outorgado(s).`);
    } catch (error) {
      console.error('Erro ao salvar grupo:', error);
      addToast('error', error.response?.data?.message || 'Erro ao salvar o grupo.');
    } finally {
      setProcessandoGrupo(false);
    }
  };

  const excluirGrupo = async (grupo) => {
    if (!window.confirm(`Excluir o grupo "${grupo.nome}"? Os clientes não são apagados.`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/grupos-outorgados/${grupo.idGrupo}`, getAuthHeader());
      await carregarGrupos();
      addToast('success', 'Grupo excluído.');
    } catch (error) {
      console.error('Erro ao excluir grupo:', error);
      addToast('error', error.response?.data?.message || 'Erro ao excluir o grupo.');
    }
  };

  // ── Validação (endereço nunca bloqueia — só nome, CPF/CNPJ e RG) ────────
  const validar = () => {
    const errosOut = {};
    if (!outorgante.nome) errosOut.nome = 'Nome é obrigatório.';
    if (!outorgante.cpfCnpj) errosOut.cpfCnpj = 'CPF/CNPJ é obrigatório.';
    setErrosOutorgante(errosOut);

    // Na representação (CRVA/DETRAN) o veículo é opcional: pode ser um primeiro emplacamento, por exemplo
    const errosVeic = {};
    if (tipoProcuracao !== TIPO_REPRESENTACAO) {
      if (!veiculo.placa) errosVeic.placa = 'Placa é obrigatória.';
      if (!veiculo.marcaModelo) errosVeic.marcaModelo = 'Marca/Modelo é obrigatório.';
      if (!veiculo.chassi) errosVeic.chassi = 'Chassi é obrigatório.';
    }
    setErrosVeiculo(errosVeic);

    let erroValor = '';
    if (tipoProcuracao === TIPO_CAUSA_PROPRIA && !(valorVenda > 0)) erroValor = 'Informe o valor da venda.';
    setErroValorVenda(erroValor);

    let erroPrazo = '';
    if (usarValidade) {
      const n = Number(validadeQtd);
      if (!Number.isInteger(n) || n < 1 || n > 999) erroPrazo = 'Informe o prazo de validade (de 1 a 999).';
    }
    setErroValidade(erroPrazo);

    const listaErrosOutorgados = outorgados.map((o) => {
      const e = {};
      if (!o.nome) e.nome = 'Nome é obrigatório.';
      if (!o.cpfCnpj) e.cpfCnpj = 'CPF/CNPJ é obrigatório.';
      return e;
    });
    setErrosOutorgados(listaErrosOutorgados);

    if (outorgados.length === 0) {
      addToast('warning', 'Adicione ao menos um outorgado.');
      return false;
    }

    const temErro =
      !!erroValor ||
      !!erroPrazo ||
      Object.keys(errosOut).length > 0 ||
      Object.keys(errosVeic).length > 0 ||
      listaErrosOutorgados.some((e) => Object.keys(e).length > 0);

    if (temErro) {
      addToast('warning', 'Preencha os campos obrigatórios destacados em vermelho.');
    }

    return !temErro;
  };

  const gerarDocumentoHtml = () => montarDocumentoHtml({
    tipo: tipoProcuracao,
    valorVenda,
    validade: usarValidade ? { quantidade: Number(validadeQtd), unidade: validadeUnidade } : null,
    empresa: dadosEmpresaCabecalho,
    outorgante,
    nacionalidade: nacionalidadeOutorgante,
    outorgados,
    veiculo,
    dataTexto: dataDocumentoTexto,
    textoCustomizado: textosCustomizados[tipoProcuracao]
  });

  const gerarProcuracao = () => {
    if (!validar()) return;
    if (mostrarPreview && editadoRef.current &&
        !window.confirm('Gerar novamente vai descartar as alterações feitas no documento. Continuar?')) {
      return;
    }
    setDocumentoHtml(sanitizarHtml(gerarDocumentoHtml()));
    setDocumentoCompacto(tipoProcuracao === TIPO_CAUSA_PROPRIA || (tipoProcuracao === TIPO_VENDA && usarValidade));
    setVersaoDocumento((v) => v + 1);
    editadoRef.current = false;
    setMostrarPreview(true);
    setTimeout(() => {
      if (previewRef.current) {
        previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const restaurarOriginal = () => {
    if (!window.confirm('Descartar as alterações e voltar ao texto original?')) return;
    setDocumentoHtml(sanitizarHtml(gerarDocumentoHtml()));
    setDocumentoCompacto(tipoProcuracao === TIPO_CAUSA_PROPRIA || (tipoProcuracao === TIPO_VENDA && usarValidade));
    setVersaoDocumento((v) => v + 1);
    editadoRef.current = false;
  };

  const imprimir = () => {
    window.print();
  };

  // Comandos de formatação do editor (estilo Word). onMouseDown + preventDefault mantém a
  // seleção do texto ao clicar nos botões.
  const comando = (nome, valor) => {
    if (editorRef.current) editorRef.current.focus();
    document.execCommand(nome, false, valor);
  };
  const botaoEditor = (nome, valor) => ({
    type: 'button',
    onMouseDown: (ev) => { ev.preventDefault(); comando(nome, valor); }
  });

  // Colar sempre como texto puro, para não trazer fontes e cores de outros documentos
  const colarTextoPuro = (ev) => {
    ev.preventDefault();
    const texto = (ev.clipboardData || window.clipboardData).getData('text');
    document.execCommand('insertText', false, texto);
  };

  const novaProcuracao = () => {
    setOutorgante(pessoaVazia());
    setImportacaoOutorgante(null);
    setVeiculosOutorgante([]);
    setVeiculo(veiculoVazio());
    setImportacaoVeiculo(null);
    setOutorgados([pessoaVazia()]);
    setOrigem({ outorgante: null, outorgados: [null], veiculo: null });
    setNacionalidadeOutorgante('brasileiro');
    setDataDocumento('');
    setErrosOutorgante({});
    setErrosVeiculo({});
    setErrosOutorgados([]);
    setValorVenda(null);
    setErroValorVenda('');
    setUsarValidade(false);
    setValidadeQtd('');
    setValidadeUnidade('DIAS');
    setErroValidade('');
    setMostrarPreview(false);
  };

  // "DATA / /" em branco para preencher à mão, ou "DATA 21 / 03 / 2025" quando a data foi informada
  const [anoDoc, mesDoc, diaDoc] = dataDocumento ? dataDocumento.split('-') : ['', '', ''];
  const dataDocumentoTexto = dataDocumento ? `${diaDoc} / ${mesDoc} / ${anoDoc}` : '\u00A0\u00A0\u00A0/\u00A0\u00A0\u00A0/';

  const situacaoOutorgante = situacaoCadastro({
    vinculado: !!outorgante.idCliente, origem: origem.outorgante, atual: outorgante, campos: CAMPOS_PESSOA,
    obrigatorios: ['nome', 'cpfCnpj'], rotuloNovo: 'Cliente ainda n\u00E3o cadastrado: ser\u00E1 criado no cadastro.',
    camposFixos: ['cpfCnpj'], rotulosCampo: ROTULOS_CAMPO_PESSOA
  });
  const situacaoOutorgado = (index) => situacaoCadastro({
    vinculado: !!outorgados[index].idCliente, origem: origem.outorgados[index], atual: outorgados[index], campos: CAMPOS_PESSOA,
    obrigatorios: ['nome', 'cpfCnpj'], rotuloNovo: 'Cliente ainda n\u00E3o cadastrado: ser\u00E1 criado no cadastro.',
    camposFixos: ['cpfCnpj'], rotulosCampo: ROTULOS_CAMPO_PESSOA
  });
  const situacaoVeiculo = situacaoCadastro({
    vinculado: !!veiculo.idVeiculo, origem: origem.veiculo, atual: veiculo, campos: CAMPOS_VEICULO_SYNC,
    obrigatorios: ['placa', 'marcaModelo'], rotuloNovo: 'Ve\u00EDculo ainda n\u00E3o cadastrado: ser\u00E1 criado no cadastro do outorgante.',
    camposFixos: ['placa'], rotulosCampo: ROTULOS_CAMPO_VEICULO
  });

  return (
    <PageBackground className="procuracao-page">
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          body * { visibility: hidden; }
          .procuracao-print-area, .procuracao-print-area * { visibility: visible; }
          .no-print { display: none !important; }
          .procuracao-page { min-height: 0 !important; background: none !important; }
          .procuracao-preview { margin: 0 !important; padding: 0 !important; max-width: none !important; }
          .procuracao-print-area {
            box-shadow: none !important; border: none !important; border-radius: 0 !important;
            padding: 16mm 28mm !important; box-sizing: border-box; width: 100%;
          }
        }
      `}</style>

      <div className="no-print">
        <Header />
      </div>

      <Content className="no-print">
        <TitleRow>
          <Title><FaFileSignature size={22} /> Procuração de Veículo</Title>
          <ConfigButton type="button" onClick={() => setModalConfigAberto(true)} title="Personalizar texto dos modelos de procuração">
            <FaCog /> Configurações
          </ConfigButton>
        </TitleRow>

        {modalConfigAberto && (
          <ModalConfiguracaoModelo
            categoria="PROCURACAO"
            modelos={MODELOS_PROCURACAO}
            tokensAjuda={TOKENS_PROCURACAO}
            buscarTextoPadrao={buscarTextoPadraoProcuracao}
            onClose={() => { setModalConfigAberto(false); carregarTextosCustomizados(); }}
          />
        )}

        {/* ── Modelo da procuração ─────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle><FaFileSignature size={16} /> Modelo da procuração</CardTitle>
          </CardHeader>
          <ModelosGrid>
            {MODELOS_PROCURACAO.map((m) => (
              <ModeloCard
                key={m.tipo}
                type="button"
                $ativo={tipoProcuracao === m.tipo}
                onClick={() => setTipoProcuracao(m.tipo)}
              >
                <strong>{m.titulo}</strong>
                <span>{m.sub}</span>
              </ModeloCard>
            ))}
          </ModelosGrid>
          {mostrarPreview && (
            <HintText>Ao trocar de modelo, clique em "Gerar Procuração" novamente para atualizar o documento.</HintText>
          )}
        </Card>


        {/* ── Outorgante ──────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle><FaUser size={16} /> Outorgante{tipoProcuracao === TIPO_AQUISICAO ? ' (comprador)' : ''}{tipoProcuracao === TIPO_CAUSA_PROPRIA ? ' (vendedor)' : ''}</CardTitle>
            <SearchButton onClick={() => abrirBusca('outorgante')}>
              <FaSearch size={12} /> Buscar Cliente Cadastrado
            </SearchButton>
          </CardHeader>

          {/* Cliente vindo da importação de CRLV-e nasce só com nome e CPF: avisa o que falta para este documento */}
          <AvisoCadastroIncompleto
            documento="procuracao"
            pessoas={[
              { rotulo: 'Outorgante', pessoa: outorgante },
              ...outorgados.map((o, i) => ({ rotulo: outorgados.length > 1 ? `Outorgado ${i + 1}` : 'Outorgado', pessoa: o })),
            ]}
          />

          <ImportarDocumento
            importacao={importacaoOutorgante}
            onImportacaoChange={setImportacaoOutorgante}
            onDadosLidos={aplicarDocumentoOutorgante}
          />

          <FormRow>
            <InputGroup style={{ flex: 2 }}>
              <Label>Nome*</Label>
              <Input name="nome" value={outorgante.nome} onChange={handleOutorganteChange} placeholder="Nome completo" hasError={!!errosOutorgante.nome} />
              {errosOutorgante.nome && <ErrorText>{errosOutorgante.nome}</ErrorText>}
            </InputGroup>
            <InputGroup>
              <Label>CPF/CNPJ*</Label>
              <Input
                name="cpfCnpj"
                value={formatarCpfCnpj(outorgante.cpfCnpj)}
                onChange={(e) => handleOutorganteChange({ target: { name: 'cpfCnpj', value: removerMascaraCPFCNPJ(e.target.value) } })}
                placeholder="000.000.000-00"
                hasError={!!errosOutorgante.cpfCnpj}
              />
              {errosOutorgante.cpfCnpj && <ErrorText>{errosOutorgante.cpfCnpj}</ErrorText>}
            </InputGroup>
          </FormRow>
          <FormRow>
            <InputGroup>
              <Label>RG</Label>
              <Input name="rg" value={outorgante.rg} onChange={handleOutorganteChange} placeholder="0000000000" />
            </InputGroup>
            <InputGroup>
              <Label>Nacionalidade (aparece no documento)</Label>
              <Input value={nacionalidadeOutorgante} onChange={(e) => setNacionalidadeOutorgante(e.target.value)} placeholder="brasileiro" />
            </InputGroup>
          </FormRow>
          <FormRow>
            <InputGroup>
              <Label>Telefone</Label>
              <Input name="telefone" value={outorgante.telefone} onChange={handleOutorganteChange} placeholder="(53) 99999-9999" />
            </InputGroup>
            <InputGroup>
              <Label>E-mail</Label>
              <Input name="email" value={outorgante.email} onChange={handleOutorganteChange} placeholder="email@exemplo.com" />
            </InputGroup>
          </FormRow>

          <SubTitle><FaMapMarkerAlt size={12} /> Endereço</SubTitle>
          <EnderecoCampos
            valor={outorgante}
            onChange={(field, value) => setOutorgante((prev) => ({ ...prev, [field]: value }))}
            onCepChange={handleOutorganteCepChange}
            buscando={buscandoCepOutorgante}
          />

          <BarraCadastro
            situacao={situacaoOutorgante} salvando={salvandoOutorgante} onSalvar={salvarClienteOutorgante}
            rotulo="Salvar dados do cliente"
          />
        </Card>

        {/* ── Veículo ─────────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle><FaCar size={16} /> Veículo{tipoProcuracao === TIPO_REPRESENTACAO ? ' (opcional)' : ''}</CardTitle>
            <SearchButton onClick={abrirModalVeiculo}>
              <FaSearch size={12} /> Selecionar Veículo Cadastrado
            </SearchButton>
          </CardHeader>

          <ImportarDocumento
            tipo="veiculo"
            importacao={importacaoVeiculo}
            onImportacaoChange={setImportacaoVeiculo}
            onDadosLidos={aplicarDocumentoVeiculo}
          />

          {veiculo.idVeiculo && (
            <ImportadoBadge>✓ Dados importados do cadastro</ImportadoBadge>
          )}

          <FormRow>
            <InputGroup style={{ flex: 2 }}>
              <Label>Marca/Modelo*</Label>
              <Input name="marcaModelo" value={veiculo.marcaModelo} onChange={handleVeiculoChange} placeholder="FORD FOCUS HC FLEX" hasError={!!errosVeiculo.marcaModelo} />
              {errosVeiculo.marcaModelo && <ErrorText>{errosVeiculo.marcaModelo}</ErrorText>}
            </InputGroup>
            <InputGroup>
              <Label>Cor</Label>
              <Input name="cor" value={veiculo.cor} onChange={handleVeiculoChange} placeholder="CINZA" />
            </InputGroup>
            <InputGroup>
              <Label>Ano</Label>
              <Input name="ano" value={veiculo.ano} onChange={handleVeiculoChange} placeholder="2020/2021" />
            </InputGroup>
          </FormRow>
          <FormRow>
            <InputGroup>
              <Label>Placa*</Label>
              <Input name="placa" value={veiculo.placa} onChange={handleVeiculoChange} placeholder="AAA-0000" style={{ textTransform: 'uppercase' }} hasError={!!errosVeiculo.placa} />
              {errosVeiculo.placa && <ErrorText>{errosVeiculo.placa}</ErrorText>}
            </InputGroup>
            <InputGroup>
              <Label>Chassi*</Label>
              <Input name="chassi" value={veiculo.chassi} onChange={handleVeiculoChange} placeholder="9BWZZZ377VT004251" style={{ textTransform: 'uppercase' }} hasError={!!errosVeiculo.chassi} />
              {errosVeiculo.chassi && <ErrorText>{errosVeiculo.chassi}</ErrorText>}
            </InputGroup>
            <InputGroup>
              <Label>Renavam</Label>
              <Input name="renavam" value={veiculo.renavam} onChange={handleVeiculoChange} placeholder="123456789" />
            </InputGroup>
          </FormRow>

          <BarraCadastro
            situacao={situacaoVeiculo} salvando={salvandoVeiculo} onSalvar={salvarVeiculoNoCadastro}
            rotulo="Salvar dados do veículo"
          />
        </Card>

        {/* ── Valor da venda (somente "Em Causa Própria") ──────────────── */}
        {tipoProcuracao === TIPO_CAUSA_PROPRIA && (
          <Card>
            <CardHeader>
              <CardTitle>Valor da venda</CardTitle>
            </CardHeader>
            <FormRow>
              <InputGroup>
                <Label>Valor*</Label>
                <NumericFormat
                  value={valorVenda ?? ''}
                  onValueChange={(v, info) => {
                    if (info.source === 'event') {
                      setValorVenda(v.floatValue ?? null);
                      setErroValorVenda('');
                    }
                  }}
                  thousandSeparator="." decimalSeparator="," decimalScale={2} fixedDecimalScale
                  prefix="R$ " placeholder="R$ 0,00"
                  customInput={Input}
                  hasError={!!erroValorVenda}
                  aria-label="Valor da venda"
                />
                {erroValorVenda && <ErrorText>{erroValorVenda}</ErrorText>}
                {valorVenda > 0 && <HintText>Por extenso: {valorPorExtenso(valorVenda)}</HintText>}
              </InputGroup>
            </FormRow>
          </Card>
        )}

        {/* ── Outorgados ──────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle><FaUser size={16} /> Outorgado(s){tipoProcuracao === TIPO_CAUSA_PROPRIA ? ' (comprador)' : ''}</CardTitle>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <SearchButton onClick={() => setModalGrupos('importar')}>
                <FaUsers size={12} /> Importar Grupo
              </SearchButton>
              <SearchButton onClick={abrirModalSalvarGrupo}>
                <FaSave size={12} /> Salvar como Grupo
              </SearchButton>
              <SearchButton onClick={adicionarOutorgado}>
                <FaPlus size={12} /> Adicionar Outorgado
              </SearchButton>
            </div>
          </CardHeader>

          {outorgados.length === 0 && (
            <EmptyState>Nenhum outorgado adicionado. Clique em "Adicionar Outorgado".</EmptyState>
          )}

          {outorgados.map((o, index) => {
            const errosO = errosOutorgados[index] || {};
            return (
              <OutorgadoBlock key={index}>
                <OutorgadoHeader>
                  <span>Outorgado {index + 1}</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <SmallSearchButton onClick={() => abrirBusca(index)}>
                      <FaSearch size={11} /> Buscar Cliente
                    </SmallSearchButton>
                    <RemoveButton onClick={() => removerOutorgado(index)} title="Remover outorgado">
                      <FaTrash size={12} />
                    </RemoveButton>
                  </div>
                </OutorgadoHeader>

                <ImportarDocumento
                  importacao={o._importacao}
                  onImportacaoChange={(imp) => atualizarOutorgado(index, '_importacao', imp)}
                  onDadosLidos={(dados) => aplicarDocumentoOutorgado(index, dados)}
                />

                <FormRow>
                  <InputGroup style={{ flex: 2 }}>
                    <Label>Nome*</Label>
                    <Input value={o.nome} onChange={(e) => atualizarOutorgado(index, 'nome', e.target.value)} placeholder="Nome completo" hasError={!!errosO.nome} />
                    {errosO.nome && <ErrorText>{errosO.nome}</ErrorText>}
                  </InputGroup>
                  <InputGroup>
                    <Label>CPF/CNPJ*</Label>
                    <Input
                      value={formatarCpfCnpj(o.cpfCnpj)}
                      onChange={(e) => atualizarOutorgado(index, 'cpfCnpj', removerMascaraCPFCNPJ(e.target.value))}
                      placeholder="000.000.000-00"
                      hasError={!!errosO.cpfCnpj}
                    />
                    {errosO.cpfCnpj && <ErrorText>{errosO.cpfCnpj}</ErrorText>}
                  </InputGroup>
                </FormRow>
                <FormRow>
                  <InputGroup>
                    <Label>RG</Label>
                    <Input value={o.rg} onChange={(e) => atualizarOutorgado(index, 'rg', e.target.value)} placeholder="0000000000" />
                  </InputGroup>
                  <InputGroup>
                    <Label>Telefone</Label>
                    <Input value={o.telefone} onChange={(e) => atualizarOutorgado(index, 'telefone', e.target.value)} placeholder="(53) 99999-9999" />
                  </InputGroup>
                  <InputGroup>
                    <Label>E-mail</Label>
                    <Input value={o.email} onChange={(e) => atualizarOutorgado(index, 'email', e.target.value)} placeholder="email@exemplo.com" />
                  </InputGroup>
                </FormRow>

                <SubTitle><FaMapMarkerAlt size={12} /> Endereço</SubTitle>
                <EnderecoCampos
                  valor={o}
                  onChange={(field, value) => atualizarOutorgado(index, field, value)}
                  onCepChange={(value) => handleOutorgadoCepChange(index, value)}
                  buscando={buscandoCepOutorgadoIndex === index}
                />

                <BarraCadastro
                  situacao={situacaoOutorgado(index)} salvando={salvandoOutorgadoIndex === index}
                  onSalvar={() => salvarClienteOutorgado(index)} rotulo="Salvar dados do cliente"
                />
              </OutorgadoBlock>
            );
          })}
        </Card>

        {/* ── Data (opcional) ──────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>Data e validade</CardTitle>
          </CardHeader>
          <FormRow>
            <InputGroup>
              <Label>Data da procuração</Label>
              <Input type="date" value={dataDocumento} onChange={(e) => setDataDocumento(e.target.value)} />
              <HintText>Deixe em branco para imprimir "DATA / /" e preencher à mão, como no modelo.</HintText>
            </InputGroup>
          </FormRow>
          <ValidadeRow>
            <label>
              <input
                type="checkbox"
                checked={usarValidade}
                onChange={(e) => { setUsarValidade(e.target.checked); setErroValidade(''); }}
              />
              Definir prazo de validade
            </label>
            {usarValidade && (
              <>
                <Input
                  type="number" min="1" max="999" step="1"
                  style={{ width: 90 }}
                  value={validadeQtd}
                  onChange={(e) => { setValidadeQtd(e.target.value); setErroValidade(''); }}
                  placeholder="90"
                  aria-label="Prazo de validade"
                  hasError={!!erroValidade}
                />
                <SelectField value={validadeUnidade} onChange={(e) => setValidadeUnidade(e.target.value)} aria-label="Unidade do prazo">
                  <option value="DIAS">dias</option>
                  <option value="MESES">meses</option>
                  <option value="ANOS">anos</option>
                </SelectField>
              </>
            )}
          </ValidadeRow>
          {erroValidade && <ErrorText>{erroValidade}</ErrorText>}
          {usarValidade && (
            <HintText>Aparece ao final do texto: "PRAZO DE VALIDADE: ... a contar da data de sua assinatura".</HintText>
          )}
        </Card>

        <ActionsRow>
          <PrimaryButton onClick={gerarProcuracao}>
            <FaCheckCircle /> Gerar Procuração
          </PrimaryButton>
          {mostrarPreview && (
            <SecondaryButton onClick={novaProcuracao}>
              <FaArrowLeft /> Nova Procuração
            </SecondaryButton>
          )}
        </ActionsRow>
      </Content>

      {/* ── Documento (preview + área de impressão) ──────────────────── */}
      {mostrarPreview && (
        <PreviewWrapper ref={previewRef} className="procuracao-preview">
          <div className="no-print">
            <PreviewToolbar>
              <span>Pré-visualização — clique no texto para editar</span>
              <PrintButton onClick={imprimir}>
                <FaPrint /> Imprimir / Salvar PDF
              </PrintButton>
            </PreviewToolbar>
            <EditorToolbar>
              <FerramentaBtn {...botaoEditor('bold')} title="Negrito (Ctrl+B)"><FaBold /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('italic')} title="Itálico (Ctrl+I)"><FaItalic /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('underline')} title="Sublinhado (Ctrl+U)"><FaUnderline /></FerramentaBtn>
              <Separador />
              <FerramentaBtn {...botaoEditor('justifyLeft')} title="Alinhar à esquerda"><FaAlignLeft /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('justifyCenter')} title="Centralizar"><FaAlignCenter /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('justifyRight')} title="Alinhar à direita"><FaAlignRight /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('justifyFull')} title="Justificar"><FaAlignJustify /></FerramentaBtn>
              <Separador />
              <FerramentaBtn {...botaoEditor('undo')} title="Desfazer (Ctrl+Z)"><FaUndo /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('redo')} title="Refazer (Ctrl+Y)"><FaRedo /></FerramentaBtn>
              <Separador />
              <FerramentaBtn
                type="button"
                onClick={restaurarOriginal}
                title="Descartar alterações e voltar ao texto original"
                style={{ width: 'auto', padding: '0 10px', gap: 6 }}
              >
                <FaEraser /> Restaurar original
              </FerramentaBtn>
            </EditorToolbar>
          </div>

          <PaperSheet
            key={versaoDocumento}
            ref={editorRef}
            className={`procuracao-print-area${documentoCompacto ? ' compacto' : ''}`}
            contentEditable
            suppressContentEditableWarning
            spellCheck
            onPaste={colarTextoPuro}
            onInput={() => { editadoRef.current = true; }}
            dangerouslySetInnerHTML={{ __html: documentoHtml }}
          />
        </PreviewWrapper>
      )}

      {/* ── Modal: importar grupo de outorgados ───────────────────────── */}
      {modalGrupos === 'importar' && (
        <ModalOverlay className="no-print">
          <ModalBox>
            <ModalHeader>
              <h3>Importar Grupo de Outorgados</h3>
              <ModalClose onClick={() => setModalGrupos(null)}><FaTimes /></ModalClose>
            </ModalHeader>
            <ModalContent>
              {grupos.length === 0 ? (
                <EmptyState>Nenhum grupo salvo. Preencha os outorgados e use "Salvar como Grupo".</EmptyState>
              ) : (
                <ResultList>
                  {grupos.map((g) => (
                    <ResultItem key={g.idGrupo} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }} onClick={() => !processandoGrupo && importarGrupo(g)}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong>{g.nome}</strong>
                        <div style={{ fontSize: 12, color: '#7f8c8d' }}>{g.membros.map((m) => m.nome).join(', ')}</div>
                      </div>
                      <RemoveButton
                        title="Excluir grupo"
                        onClick={(ev) => { ev.stopPropagation(); excluirGrupo(g); }}
                      >
                        <FaTrash size={12} />
                      </RemoveButton>
                    </ResultItem>
                  ))}
                </ResultList>
              )}
              {processandoGrupo && <HintText>Importando...</HintText>}
            </ModalContent>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ── Modal: salvar os outorgados atuais como grupo ─────────────── */}
      {modalGrupos === 'salvar' && (
        <ModalOverlay className="no-print">
          <ModalBox>
            <ModalHeader>
              <h3>Salvar Outorgados como Grupo</h3>
              <ModalClose onClick={() => setModalGrupos(null)}><FaTimes /></ModalClose>
            </ModalHeader>
            <ModalContent>
              <Label>Nome do grupo</Label>
              <SearchRow>
                <Input
                  value={nomeGrupo}
                  onChange={(e) => setNomeGrupo(e.target.value)}
                  placeholder="Ex.: Loja Central Car"
                  maxLength={100}
                  onKeyPress={(e) => e.key === 'Enter' && salvarGrupo()}
                />
                <PrimaryButton onClick={salvarGrupo} disabled={processandoGrupo}>
                  <FaSave /> {processandoGrupo ? 'Salvando...' : 'Salvar'}
                </PrimaryButton>
              </SearchRow>
              <HintText>Serão salvos: {outorgadosPreenchidos().map((o) => o.nome || o.cpfCnpj).join(', ')}.</HintText>
              <HintText>Todos precisam estar no cadastro de clientes. Um grupo com o mesmo nome é atualizado.</HintText>
            </ModalContent>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ── Modal de Busca de Cliente (outorgante / outorgados) ───────── */}
      {modalBuscaAberto && (
        <ModalOverlay className="no-print">
          <ModalBox>
            <ModalHeader>
              <h3>Buscar Cliente Cadastrado</h3>
              <ModalClose onClick={() => setModalBuscaAberto(false)}><FaTimes /></ModalClose>
            </ModalHeader>
            <ModalContent>
              <SearchRow>
                <Input
                  value={termoBusca}
                  onChange={(e) => setTermoBusca(e.target.value)}
                  placeholder="Nome ou CPF/CNPJ"
                  onKeyPress={(e) => e.key === 'Enter' && executarBusca()}
                />
                <PrimaryButton onClick={executarBusca} disabled={buscando}>
                  <FaSearch /> {buscando ? 'Buscando...' : 'Buscar'}
                </PrimaryButton>
              </SearchRow>

              {resultadosBusca.length > 0 ? (
                <ResultList>
                  {resultadosBusca.map((c) => (
                    <ResultItem key={c.idCliente} onClick={() => selecionarResultado(c)}>
                      <strong>{c.nome}</strong>
                      <span>{formatarCpfCnpj(c.cpfCnpj)}</span>
                    </ResultItem>
                  ))}
                </ResultList>
              ) : (
                <EmptyState>Nenhum resultado. Digite um nome ou CPF/CNPJ e busque.</EmptyState>
              )}
            </ModalContent>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ── Modal de Seleção de Veículo (do outorgante ou por placa) ──── */}
      {modalVeiculoAberto && (
        <ModalOverlay className="no-print">
          <ModalBox>
            <ModalHeader>
              <h3>Selecionar Veículo</h3>
              <ModalClose onClick={() => setModalVeiculoAberto(false)}><FaTimes /></ModalClose>
            </ModalHeader>
            <ModalContent>
              {veiculosOutorgante.length > 0 && (
                <>
                  <SubTitle>Veículos do outorgante</SubTitle>
                  <ResultList style={{ marginBottom: '18px' }}>
                    {veiculosOutorgante.map((v) => (
                      <ResultItem key={v.idVeiculo} onClick={() => importarVeiculo(v)}>
                        <strong>{v.marcaModelo}</strong>
                        <span>Placa {v.placa}{v.chassi ? ` · Chassi ${v.chassi}` : ''}</span>
                      </ResultItem>
                    ))}
                  </ResultList>
                </>
              )}

              <SubTitle>Buscar veículo cadastrado por placa</SubTitle>
              <SearchRow>
                <Input
                  value={placaBusca}
                  onChange={(e) => setPlacaBusca(e.target.value.toUpperCase())}
                  placeholder="AAA-0000"
                  onKeyPress={(e) => e.key === 'Enter' && buscarVeiculoPorPlaca()}
                />
                <PrimaryButton onClick={buscarVeiculoPorPlaca} disabled={buscandoPlaca}>
                  <FaSearch /> {buscandoPlaca ? 'Buscando...' : 'Buscar'}
                </PrimaryButton>
              </SearchRow>

              {erroPlaca && <EmptyState>{erroPlaca}</EmptyState>}

              {resultadoPlaca && (
                <ResultList>
                  <ResultItem onClick={() => importarVeiculo(resultadoPlaca)}>
                    <strong>{resultadoPlaca.marcaModelo} — {resultadoPlaca.placa}</strong>
                    <span>
                      {resultadoPlaca.cliente?.nome
                        ? `Cadastrado para: ${resultadoPlaca.cliente.nome}`
                        : 'Clique para importar os dados'}
                    </span>
                  </ResultItem>
                </ResultList>
              )}
            </ModalContent>
          </ModalBox>
        </ModalOverlay>
      )}

      <ConfirmModal
        isOpen={!!confirmacaoCadastro}
        onClose={() => setConfirmacaoCadastro(null)}
        onConfirm={() => { if (confirmacaoCadastro) confirmacaoCadastro.onConfirm(); }}
        title={confirmacaoCadastro ? confirmacaoCadastro.titulo : ''}
        message={confirmacaoCadastro ? confirmacaoCadastro.mensagem : ''}
        confirmText={confirmacaoCadastro ? confirmacaoCadastro.textoConfirmar : 'Confirmar'}
        cancelText="Cancelar"
      />
    </PageBackground>
  );
};

// ─── Styled Components ────────────────────────────────────────────────────────

export const PageBackground = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #e6f7f5 0%, #d4efed 100%);
`;

export const Content = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const Title = styled.h1`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #004d4d;
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
  font-family: 'Segoe UI', Arial, sans-serif;
`;

export const ConfigButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  background: white;
  color: #004d4d;
  border: 1.5px solid #0f766e;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

export const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 128, 128, 0.15);
  padding: 20px;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
`;

export const CardTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  color: #004d4d;
  margin: 0;
  font-weight: 700;
`;

export const SubTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 700;
  color: #008080;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 10px 0 10px 0;
`;

export const SearchButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #008080 0%, #20b2aa 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s;
  &:hover { transform: translateY(-1px); }
`;

const SmallSearchButton = styled(SearchButton)`
  padding: 6px 10px;
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 8px;
  color: #d32f2f;
  cursor: pointer;
`;

const ImportadoBadge = styled.div`
  display: inline-block;
  background: #e8f5e9;
  color: #2e7d32;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 8px;
  margin-bottom: 12px;
`;

export const FormRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 14px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 10px;
  }
`;

export const InputGroup = styled.div`
  flex: 1;
  min-width: 140px;
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 13px;
  color: #34495e;
`;

export const HintText = styled.span`
  display: block;
  color: #7f8c8d;
  font-size: 11.5px;
  margin-top: 4px;
  font-style: italic;
`;

export const ErrorText = styled.span`
  display: block;
  color: #e74c3c;
  font-size: 12px;
  margin-top: 4px;
`;

export const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${(props) => (props.hasError ? '#e74c3c' : '#ddd')};
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? '#e74c3c' : '#008080')};
    box-shadow: 0 0 0 3px ${(props) => (props.hasError ? 'rgba(231,76,60,0.1)' : 'rgba(0,128,128,0.1)')};
  }
`;

const OutorgadoBlock = styled.div`
  border: 1px dashed #cfd8dc;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 14px;
  &:last-child { margin-bottom: 0; }
`;

const OutorgadoHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  font-weight: 700;
  color: #008080;
  font-size: 13px;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 20px;
  color: #7f8c8d;
  font-size: 13px;
`;

export const ActionsRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

export const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  background: linear-gradient(135deg, #004d4d 0%, #008080 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 4px 14px rgba(0,128,128,0.35); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  background: #f1f3f5;
  color: #34495e;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  &:hover { background: #e9ecef; }
`;

// ── Preview / documento impresso ────────────────────────────────────────────

export const PreviewWrapper = styled.div`
  max-width: 842px;
  margin: 0 auto 40px auto;
  padding: 0 24px;
`;

export const PreviewToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  padding: 12px 18px;
  border-radius: 10px 10px 0 0;
  border: 1px solid rgba(0,128,128,0.15);
  border-bottom: none;
  font-weight: 700;
  color: #004d4d;
  font-size: 14px;
`;

export const PrintButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #008080 0%, #20b2aa 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
`;

const ValidadeRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 6px;
  label { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: #34495e; cursor: pointer; }
`;

export const SelectField = styled.select`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  background: white;
`;

export const ModelosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 12px;
`;

export const ModeloCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
  padding: 14px;
  background: ${(props) => (props.$ativo ? '#f0fdfa' : 'white')};
  border: 2px solid ${(props) => (props.$ativo ? '#008080' : '#e5e7eb')};
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  strong { font-size: 14px; color: #004d4d; }
  span { font-size: 12px; color: #7f8c8d; }
  &:hover { border-color: #008080; }
`;

export const EditorToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  background: #f8f9fa;
  padding: 8px 14px;
  border: 1px solid rgba(0,128,128,0.15);
  border-bottom: none;
`;

export const FerramentaBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 30px;
  background: white;
  color: #34495e;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  &:hover { background: #e6f4f4; color: #004d4d; }
`;

export const Separador = styled.span`
  width: 1px;
  height: 22px;
  background: #ced4da;
  margin: 0 4px;
`;

// Folha A4 no formato do modelo: fonte sem serifa (estilo Calibri), texto justificado, margens largas.
// O conteúdo é HTML puro (editável), estilizado pelos seletores abaixo.
export const PaperSheet = styled.div`
  background: white;
  padding: 16mm 26mm;
  border: 1px solid rgba(0,128,128,0.15);
  border-radius: 0 0 10px 10px;
  box-shadow: 0 6px 24px rgba(0,0,0,0.1);
  font-family: Calibri, Carlito, 'Segoe UI', Arial, sans-serif;
  font-size: 11pt;
  color: #111;
  line-height: 1.5;
  outline: none;
  min-height: 200px;
  cursor: text;

  &:focus { box-shadow: 0 6px 24px rgba(0,0,0,0.1), 0 0 0 2px rgba(0,128,128,0.35); }

  /* Cabeçalho do escritório: identificação pequena, em negrito, alinhada à esquerda */
  .cab { text-align: left; margin-bottom: 22px; }
  .cab p {
    font-family: Arial, Helvetica, sans-serif;
    font-weight: 700;
    font-size: 7pt;
    line-height: 1.3;
    text-align: left;
    margin: 0;
    text-transform: uppercase;
  }

  h1 { text-align: center; font-size: 16pt; font-weight: 700; margin: 0 0 24px 0; text-transform: uppercase; }
  p.par { text-align: justify; font-size: 11pt; margin: 0 0 14px 0; }

  /* Dados do veículo um embaixo do outro (placa, chassi, renavam, marca, cor), rótulos em negrito */
  .veiculo {
    margin: 14px 0 18px 0;
    font-size: 11.5pt;
    line-height: 1.45;
  }

  /* Modelo mais longo (causa própria): tipografia um pouco mais compacta para caber em uma página A4 */
  &.compacto p.par { font-size: 10.5pt; line-height: 1.4; margin-bottom: 9px; }
  &.compacto .veiculo { margin: 8px 0 12px 0; font-size: 11pt; line-height: 1.35; }
  &.compacto .fecha { margin-top: 14px; }
  &.compacto .fecha .data { margin-bottom: 28px; }

  /* Data em branco e linha de assinatura à direita, como no modelo */
  .fecha { width: 62%; margin-left: auto; text-align: center; margin-top: 26px; page-break-inside: avoid; }
  /* Local e data numa linha só (nunca quebra no meio da data): a linha é mais larga que o bloco, igualmente
     para os dois lados, então continua centralizada sobre a assinatura mesmo com cidade de nome longo */
  .fecha .data { font-size: 11pt; margin: 0 -50% 40px -50%; white-space: nowrap; }
  .fecha .linha { border-top: 1px solid #333; margin-bottom: 6px; }
  .fecha span { font-size: 11pt; }
`;

// ── Modais ────────────────────────────────────────────────────────────────────

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ModalBox = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  h3 { margin: 0; font-size: 16px; color: #2c3e50; }
`;

export const ModalClose = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #7f8c8d;
`;

export const ModalContent = styled.div`
  padding: 18px 20px;
`;

export const SearchRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
`;

export const ResultList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ResultItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  border: 1px solid #eee;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  strong { font-size: 14px; color: #2c3e50; }
  span { font-size: 12.5px; color: #7f8c8d; }
  &:hover { background: #f1f8fe; border-color: #008080; }
`;

export default Procuracao;
